import React from "react";
import styles from "../style.module.scss";
import { Label } from "semantic-ui-react";

/**
 * Endpoint renderer
 *
 * Usage:
 *   <Endpoint spec={clubsGetSpec} />
 *
 * Expected spec shape (example):
 * {
 *   method: "GET",
 *   path: "/clubs",
 *   description: ["...", "..."] OR "..."
 *   pathParams: [{ name, type, required, description }],
 *   queryParams: [{ name, type, required, description, default }],
 *   bodyParams: [{ name, type, required, description, default }],
 *   bodyExamples: [{ example (object) }],
 *   responses: [{ status, label, example (object) }],
 *   fields: [{ field, type, description }]
 * }
 */
export default function Endpoint({ spec }) {
    if (!spec) return null;

    const {
        method,
        path,
        description,
        pathParams = [],
        queryParams = [],
        bodyParams = [],
        bodyExamples = [],
        responses = [],
        fields = [],
    } = spec;

    const descLines =
        typeof description === "string" ? [description] : description || [];

    return (
        <>
            {/* Header */}
            <div
                className={styles.endpointHeader}
                id={endpointAnchorId(method, path)}
            >
                <Label size='large' color={getMethodColor(method)}>
                    {method}
                </Label>
                <span className={styles.endpointPath}>{path}</span>
            </div>

            {/* Description */}
            {descLines.map((line, idx) => (
                <p key={idx} style={idx > 0 ? { marginTop: 10 } : undefined}>
                    {line}
                </p>
            ))}

            {/* Path params */}
            {pathParams.length !== 0 && (
                <>
                    <h3>Path parameters</h3>
                    <ParamsTable rows={pathParams} />
                </>
            )}

            {/* Query params */}
            {queryParams.length !== 0 && (
                <>
                    <h3>Query parameters</h3>
                    <ParamsTable rows={queryParams} />
                </>
            )}

            {/* Body params */}
            {bodyParams.length !== 0 && (
                <>
                    <h3>Body parameters</h3>
                    <ParamsTable rows={bodyParams} />
                </>
            )}

            {/* Responses */}
            {bodyExamples.length !== 0 && (
                <>
                    <h3>Body Examples</h3>
                    {bodyExamples.map((r, idx) => {
                        const code = getResponseExample(r);
                        return (
                            <div key={idx} style={{ marginBottom: 20 }}>
                                {code ? (
                                    <div className={styles.codeWrap}>
                                        <pre
                                            className={`${styles.code} ${
                                                r.containsTruncatedData
                                                    ? styles.truncatedWarning
                                                    : ""
                                            }`}
                                        >
                                            {code}
                                        </pre>

                                        {r.containsTruncatedData ? (
                                            <div
                                                className={
                                                    styles.truncatedWarning
                                                }
                                            >
                                                <strong>
                                                    This example contains
                                                    truncated data samples.
                                                    Please run a real request to
                                                    get accurate data.
                                                </strong>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </>
            )}

            {/* Responses */}
            {responses.length !== 0 && (
                <>
                    <h3>Responses</h3>
                    {responses.map((r, idx) => {
                        const code = getResponseExample(r);
                        return (
                            <div key={idx} style={{ marginBottom: 20 }}>
                                {code ? (
                                    <div className={styles.codeWrap}>
                                        {/* Code header (attached to top of pre) */}
                                        <div
                                            className={styles.codeHeader}
                                            style={{
                                                backgroundColor: statusBg10(
                                                    getStatusColor(r.status),
                                                ),
                                            }}
                                        >
                                            {" "}
                                            <Label
                                                basic
                                                color={getStatusColor(r.status)}
                                            >
                                                {r.status}
                                            </Label>
                                            <span className='ml-2'>
                                                {r.label || ""}
                                            </span>
                                        </div>
                                        {r.containsTruncatedData ? (
                                            <div
                                                className={
                                                    styles.truncatedWarning
                                                }
                                            >
                                                <strong>
                                                    This example contains
                                                    truncated / placeholder data
                                                    samples. Please run a real
                                                    request to get accurate
                                                    data.
                                                </strong>
                                            </div>
                                        ) : null}

                                        <pre
                                            className={`${styles.code} ${
                                                r.containsTruncatedData
                                                    ? styles.truncatedWarning
                                                    : ""
                                            }`}
                                        >
                                            {code}
                                        </pre>
                                    </div>
                                ) : (
                                    // No example case → still show header
                                    <div className={styles.codeHeader}>
                                        <Label
                                            basic
                                            color={getStatusColor(r.status)}
                                        >
                                            {r.status}
                                        </Label>
                                        <span className='ml-2'>
                                            {r.label || ""}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            {/* Additional info */}
            {fields.length !== 0 && (
                <>
                    <h3>Response information</h3>
                    <FieldsTable rows={fields} />
                </>
            )}
        </>
    );
}

/* ------------------------- Helpers / Tables ------------------------- */

function getStatusColor(status) {
    const code = Number(status);

    if (code >= 200 && code < 300) return "green"; // Success
    if (code >= 400 && code < 500) return "red"; // Client errors

    return "grey"; // fallback / unknown
}

function statusBg10(color) {
    // 10% alpha backgrounds approximating semantic-ui label colours
    switch (color) {
        case "green":
            return "rgba(33, 186, 69, 0.10)";
        case "red":
            return "rgba(219, 40, 40, 0.10)";
        default:
            return "rgba(118, 118, 118, 0.10)";
    }
}

function endpointAnchorId(method, path) {
    return `endpoint-${method}-${path}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

function ParamsTable({ rows }) {
    return (
        <table className={styles.apiTable}>
            <thead>
                <tr>
                    <th>Parameter</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((p, idx) => (
                    <tr key={idx}>
                        <td>
                            <Label>{p.name}</Label>
                        </td>
                        <td>
                            <Label basic>{p.type || "—"}</Label>
                        </td>
                        <td>
                            {p.required ? (
                                <Label basic color='red'>
                                    Yes
                                </Label>
                            ) : (
                                <Label basic>No</Label>
                            )}
                        </td>
                        <td>
                            {Array.isArray(p.description)
                                ? p.description.map((line, i) => (
                                      <div
                                          key={i}
                                          style={
                                              i > 0
                                                  ? { marginTop: 10 }
                                                  : undefined
                                          }
                                      >
                                          {line}
                                      </div>
                                  ))
                                : p.description || "—"}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function FieldsTable({ rows }) {
    return (
        <table className={styles.apiTable}>
            <thead>
                <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((f, idx) => (
                    <tr key={idx}>
                        <td>
                            <Label>{f.field}</Label>
                        </td>
                        <td>
                            <Label basic>{f.type || "—"}</Label>
                        </td>
                        <td>
                            {Array.isArray(f.description)
                                ? f.description.map((line, i) => (
                                      <div
                                          key={i}
                                          style={
                                              i > 0
                                                  ? { marginTop: 10 }
                                                  : undefined
                                          }
                                      >
                                          {line}
                                      </div>
                                  ))
                                : f.description || "—"}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function getResponseExample(r) {
    if (r.example && typeof r.example === "object") {
        try {
            const resolved = resolveDateTokens(r.example);
            return JSON.stringify(resolved, null, 4);
        } catch (e) {
            return String(r.example);
        }
    }

    return "";
}

/**
 * Recursively walks an object/array and replaces any:
 *   { "$date": "<expression>" }
 * with an ISO string.
 */
function resolveDateTokens(value) {
    if (Array.isArray(value)) {
        return value.map(resolveDateTokens);
    }

    if (value && typeof value === "object") {
        // Date token leaf (supports ISO default + requested parts)
        if (
            Object.prototype.hasOwnProperty.call(value, "$date") &&
            typeof value.$date === "string"
        ) {
            const d = parseDateExpression(value.$date);

            // If date expression can't be parsed, keep old behaviour (null)
            if (!d) return null;

            // Backwards compatible: if it's exactly { $date: "..." } => ISO string
            const keys = Object.keys(value);
            if (keys.length === 1) {
                return d.toISOString();
            }

            // Multi-part request: { $date: "...", $parts: ["day","month","year"] }
            if (Array.isArray(value.$parts) && value.$parts.length) {
                const out = {};
                for (const part of value.$parts) {
                    out[part] = getDatePartUTC(d, part);
                }
                return out;
            }

            // Single-part requests: $day / $month / $year
            if (value.$day) return getDatePartUTC(d, "day");
            if (value.$month) return getDatePartUTC(d, "month");
            if (value.$year) return getDatePartUTC(d, "year");

            // Fallback: if unknown extras, still default to ISO
            return d.toISOString();
        }

        // Regular object
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = resolveDateTokens(v);
        }
        return out;
    }

    return value;
}

function getDatePartUTC(d, part) {
    switch (String(part).toLowerCase()) {
        case "day":
            return d.getUTCDate(); // 1-31
        case "month":
            return d.getUTCMonth() + 1; // 1-12
        case "year":
            return d.getUTCFullYear(); // 2026
        default:
            return null;
    }
}

/**
 * Supported expressions:
 * - "today+Nd@HH:MM" (local time)
 * - "today-Nd@HH:MM:SS.mmm" (local time)
 * - "now+Nh" / "now-Nh" (relative hours)
 */
function parseDateExpression(expr) {
    const s = String(expr).trim().toLowerCase();

    // now+2h / now-3h
    {
        const m = s.match(/^now\s*([+-])\s*(\d+)\s*h$/);
        if (m) {
            const sign = m[1] === "+" ? 1 : -1;
            const hours = Number(m[2]) * sign;
            const d = new Date();
            d.setHours(d.getHours() + hours);
            return d;
        }
    }

    // today@monthStart
    // today@monthEnd
    // today@12:00
    // today+10d@23:59:59.999
    // today-3d
    {
        const m = s.match(/^today(?:\s*([+-])\s*(\d+)\s*d)?(?:@(.+))?$/);
        if (m) {
            const sign = m[1] === "-" ? -1 : 1;
            const days = m[2] ? Number(m[2]) * sign : 0;

            const timePartRaw = (m[3] || "00:00").trim();
            const timeKey = timePartRaw.toLowerCase();

            // Base "today" in UTC (00:00Z), then apply day offset in UTC.
            const now = new Date();
            const y = now.getUTCFullYear();
            const mo = now.getUTCMonth();
            const da = now.getUTCDate();

            const baseMsUtc = Date.UTC(y, mo, da + days, 0, 0, 0, 0);
            const base = new Date(baseMsUtc);

            // Month boundary tokens (based on the month of the base date)
            if (timeKey === "monthstart" || timeKey === "startofmonth") {
                const by = base.getUTCFullYear();
                const bmo = base.getUTCMonth();
                return new Date(Date.UTC(by, bmo, 1, 0, 0, 0, 0));
            }

            if (timeKey === "monthend" || timeKey === "endofmonth") {
                const by = base.getUTCFullYear();
                const bmo = base.getUTCMonth();
                return new Date(Date.UTC(by, bmo + 1, 0, 23, 59, 59, 999));
            }

            // Normal explicit time (HH:MM[:SS[.mmm]])
            const t = parseTimeParts(timePartRaw);
            return new Date(
                Date.UTC(
                    base.getUTCFullYear(),
                    base.getUTCMonth(),
                    base.getUTCDate(),
                    t.hh,
                    t.mm,
                    t.ss,
                    t.ms,
                ),
            );
        }
    }

    return null;
}

function parseTimeParts(timePart) {
    const m = timePart.match(
        /^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
    );
    if (!m) {
        return { hh: 0, mm: 0, ss: 0, ms: 0 };
    }

    const hh = clampInt(m[1], 0, 23);
    const mm = clampInt(m[2], 0, 59);
    const ss = m[3] ? clampInt(m[3], 0, 59) : 0;
    const ms = m[4] ? clampInt(m[4].padEnd(3, "0"), 0, 999) : 0;

    return { hh, mm, ss, ms };
}

function clampInt(n, min, max) {
    const v = Number(n);
    if (Number.isNaN(v)) return min;
    return Math.max(min, Math.min(max, v));
}

function getMethodColor(method) {
    let colour = "green"; // default for GET

    switch ((method || "").toUpperCase()) {
        case "POST":
            colour = "blue";
            break;
        case "PUT":
            colour = "purple";
            break;
        case "PATCH":
            colour = "yellow";
            break;
        case "DELETE":
            colour = "red";
            break;
        default:
            colour = "green";
    }

    return colour;
}
